/**
 * TopicDrilldownModal
 * Professional drill-down modal showing a single topic's distribution
 * across tenants and agents with charts + timeseries.
 */

import { useState, useEffect, useMemo } from 'react';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { Modal, Typography, Spin, Empty, theme } from 'antd';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { TopicDrilldown } from '../types/insights';
import insightsService from '../services/insightsService';
import { useChartTheme, tooltipStyle as makeTooltipStyle, axisTickStyle, gridProps, PALETTE } from '../utils/chartTheme';

const { Text } = Typography;

/** Expand comma-separated chatbot IDs (from grouped agent options) into individual IDs */
function expandChatbotIds(ids: string[]): string[] {
  return ids.flatMap(id => id.split(','));
}

function formatBucket(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' });
}

interface Props {
  open: boolean;
  tag: string | null;
  onClose: () => void;
  filters: { dateFrom: string; dateTo: string; tenantIds?: string[]; chatbotIds?: string[] };
}

export function TopicDrilldownModal({ open, tag, onClose, filters }: Props) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const ct = useChartTheme();
  const [data, setData] = useState<TopicDrilldown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !tag) return;
    setLoading(true);
    setData(null);
    insightsService
      .getTopicDrilldown(tag, {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        tenant_ids: filters.tenantIds,
        chatbot_ids: filters.chatbotIds ? expandChatbotIds(filters.chatbotIds) : undefined,
      })
      .then(setData)
      .catch((err) => console.error('[TopicDrilldown] Failed:', err))
      .finally(() => setLoading(false));
  }, [open, tag, filters.dateFrom, filters.dateTo, filters.tenantIds, filters.chatbotIds]);

  const tenantChartData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.tenant_distribution).map(([name, count]) => ({ name, count }));
  }, [data]);

  const agentChartData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.agent_distribution).map(([name, count]) => ({ name, count }));
  }, [data]);

  const timeseriesData = useMemo(() => {
    if (!data) return [];
    return data.timeseries.buckets.map((b, i) => ({
      date: formatBucket(b),
      count: data.timeseries.counts[i],
    }));
  }, [data]);

  const tooltipContentStyle = makeTooltipStyle(ct);
  const axisStyle = axisTickStyle(ct);
  const gridStyle = gridProps(ct);

  const sectionCardStyle: React.CSSProperties = {
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadiusLG,
    padding: '16px',
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: ct.mutedText,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    display: 'block',
    marginBottom: 12,
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 6,
              height: 22,
              borderRadius: 3,
              background: 'linear-gradient(180deg, #8b5cf6 0%, #6d28d9 100%)',
            }}
          />
          <span style={{ fontSize: 16, fontWeight: 600 }}>Topic: {tag}</span>
          {data && (
            <span style={{ fontSize: 13, color: ct.mutedText, fontWeight: 400, marginInlineStart: 8 }}>
              {data.total} occurrences
            </span>
          )}
        </div>
      }
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : !data || data.total === 0 ? (
        <Empty description={t('insights.topicDrilldown.noDataForTopic')} style={{ padding: '40px 0' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '8px 0' }}>
          {/* Timeseries */}
          <div style={sectionCardStyle}>
            <Text style={sectionLabelStyle}>{t('insights.topicDrilldown.frequencyOverTime')}</Text>
            <svg width={0} height={0}>
              <defs>
                <linearGradient id="drilldownGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
                </linearGradient>
              </defs>
            </svg>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={timeseriesData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey="date" tick={axisStyle} tickLine={false} axisLine={{ stroke: ct.xAxisLine }} />
                <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipContentStyle} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#drilldownGrad)"
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Distribution row */}
          <div style={{ display: 'flex', gap: 20 }}>
            {/* By tenant */}
            <div style={{ ...sectionCardStyle, flex: 1 }}>
              <Text style={sectionLabelStyle}>{t('insights.topicDrilldown.byTenant')}</Text>
              {tenantChartData.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('insights.topicDrilldown.noTenantData')} />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(tenantChartData.length * 36, 120)}>
                  <BarChart
                    data={tenantChartData}
                    layout="vertical"
                    margin={{ top: 0, right: 24, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid {...gridStyle} horizontal={false} />
                    <XAxis type="number" tick={axisStyle} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={axisStyle}
                      tickLine={false}
                      axisLine={false}
                      width={120}
                    />
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      formatter={(value: number) => {
                        const pct = data.total > 0 ? ((value / data.total) * 100).toFixed(1) : '0';
                        return [`${value} (${pct}%)`, 'Count'];
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                      {tenantChartData.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* By agent */}
            <div style={{ ...sectionCardStyle, flex: 1 }}>
              <Text style={sectionLabelStyle}>{t('insights.topicDrilldown.byAgent')}</Text>
              {agentChartData.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('insights.topicDrilldown.noAgentData')} />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(agentChartData.length * 36, 120)}>
                  <BarChart
                    data={agentChartData}
                    layout="vertical"
                    margin={{ top: 0, right: 24, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid {...gridStyle} horizontal={false} />
                    <XAxis type="number" tick={axisStyle} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={axisStyle}
                      tickLine={false}
                      axisLine={false}
                      width={120}
                    />
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      formatter={(value: number) => {
                        const pct = data.total > 0 ? ((value / data.total) * 100).toFixed(1) : '0';
                        return [`${value} (${pct}%)`, 'Count'];
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                      {agentChartData.map((_, i) => (
                        <Cell key={i} fill={PALETTE[(i + 5) % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
